import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Users,
  FileText,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';

interface KycMetrics {
  pending: number;
  inReview: number;
  approved: number;
  rejected: number;
  additionalDocsRequired: number;
  total: number;
  approvalRate: number;
  avgAnalysisTimeHours: number;
  slaCumprimento: number;
  pendingChange: number;
  approvedChange: number;
}

interface SlaAlert {
  id: string;
  virtualAccountId: string;
  userName: string;
  kycType: string;
  submittedAt: string;
  slaDeadline: string;
  hoursRemaining: number;
  status: 'warning' | 'critical';
}

interface TrendDataPoint {
  date: string;
  submitted: number;
  approved: number;
  rejected: number;
}

interface RejectionReason {
  reason: string;
  count: number;
  percentage: number;
}

type PeriodType = '7d' | '30d' | '90d';

export function KycDashboard() {
  const [period, setPeriod] = useState<PeriodType>('30d');

  const { data: metrics, isLoading: loadingMetrics, refetch: refetchMetrics } = useQuery<KycMetrics>({
    queryKey: ['kyc-dashboard-metrics', period],
    queryFn: async () => {
      const response = await api.get(`/admin/kyc/dashboard/metrics?period=${period}`);
      return response.data;
    },
  });

  const { data: slaAlerts, isLoading: loadingAlerts } = useQuery<SlaAlert[]>({
    queryKey: ['kyc-sla-alerts'],
    queryFn: async () => {
      const response = await api.get('/admin/kyc/dashboard/sla-alerts');
      return response.data;
    },
  });

  const { data: trends, isLoading: loadingTrends } = useQuery<TrendDataPoint[]>({
    queryKey: ['kyc-trends', period],
    queryFn: async () => {
      const response = await api.get(`/admin/kyc/reports/trends?days=${period === '7d' ? 7 : period === '30d' ? 30 : 90}`);
      return response.data;
    },
  });

  const { data: rejectionReasons } = useQuery<RejectionReason[]>({
    queryKey: ['kyc-rejection-reasons'],
    queryFn: async () => {
      const response = await api.get('/admin/kyc/reports/top-rejection-reasons?limit=5');
      return response.data;
    },
  });

  const statusData = metrics ? [
    { name: 'Pendentes', value: metrics.pending, color: '#3b82f6' },
    { name: 'Em Análise', value: metrics.inReview, color: '#eab308' },
    { name: 'Aprovados', value: metrics.approved, color: '#22c55e' },
    { name: 'Rejeitados', value: metrics.rejected, color: '#ef4444' },
    { name: 'Docs Adicionais', value: metrics.additionalDocsRequired, color: '#f97316' },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100 flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-primary-400" />
            Dashboard KYC
          </h1>
          <p className="text-surface-400 mt-1">
            Acompanhe métricas e status das verificações KYC
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as PeriodType)}
            className="px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
          >
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="90d">Últimos 90 dias</option>
          </select>
          <button
            onClick={() => refetchMetrics()}
            className="p-2 bg-surface-800 rounded-lg text-surface-400 hover:text-surface-100 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      {loadingMetrics ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-surface-800 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard
            title="Pendentes"
            value={metrics?.pending || 0}
            change={metrics?.pendingChange}
            icon={Clock}
            color="blue"
          />
          <MetricCard
            title="Aprovados"
            value={metrics?.approved || 0}
            change={metrics?.approvedChange}
            icon={CheckCircle2}
            color="green"
          />
          <MetricCard
            title="Taxa de Aprovação"
            value={`${metrics?.approvalRate?.toFixed(1) || 0}%`}
            icon={TrendingUp}
            color="cyan"
          />
          <MetricCard
            title="Tempo Médio"
            value={`${metrics?.avgAnalysisTimeHours?.toFixed(1) || 0}h`}
            icon={Clock}
            color="purple"
          />
        </div>
      )}

      {/* SLA Alerts */}
      {slaAlerts && slaAlerts.length > 0 && (
        <div className="glass rounded-xl p-6 border border-yellow-500/30">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-yellow-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Alertas de SLA ({slaAlerts.length})
            </h2>
            <Link
              to="/admin/kyc/queue?filter=sla"
              className="text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1"
            >
              Ver todos <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-2">
            {slaAlerts.slice(0, 5).map((alert) => (
              <div
                key={alert.id}
                className={cn(
                  "flex items-center justify-between p-3 rounded-lg",
                  alert.status === 'critical' ? "bg-red-500/10" : "bg-yellow-500/10"
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    alert.status === 'critical' ? "bg-red-500" : "bg-yellow-500"
                  )} />
                  <div>
                    <p className="text-sm font-medium text-surface-100">{alert.userName}</p>
                    <p className="text-xs text-surface-400">KYC {alert.kycType}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={cn(
                    "text-sm font-medium",
                    alert.status === 'critical' ? "text-red-400" : "text-yellow-400"
                  )}>
                    {alert.hoursRemaining}h restantes
                  </p>
                  <p className="text-xs text-surface-500">
                    Deadline: {new Date(alert.slaDeadline).toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend Chart */}
        <div className="glass rounded-xl p-6">
          <h2 className="text-lg font-semibold text-surface-100 mb-4">Tendência de Submissões</h2>
          {loadingTrends ? (
            <div className="h-64 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
                <YAxis stroke="#9ca3af" fontSize={12} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                  labelStyle={{ color: '#f3f4f6' }}
                />
                <Area type="monotone" dataKey="submitted" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} name="Submetidos" />
                <Area type="monotone" dataKey="approved" stackId="2" stroke="#22c55e" fill="#22c55e" fillOpacity={0.3} name="Aprovados" />
                <Area type="monotone" dataKey="rejected" stackId="3" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} name="Rejeitados" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Status Distribution */}
        <div className="glass rounded-xl p-6">
          <h2 className="text-lg font-semibold text-surface-100 mb-4">Distribuição por Status</h2>
          {loadingMetrics ? (
            <div className="h-64 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {statusData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-sm text-surface-400">{item.name}</span>
                    </div>
                    <span className="text-sm font-medium text-surface-100">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Rejection Reasons */}
        <div className="glass rounded-xl p-6">
          <h2 className="text-lg font-semibold text-surface-100 mb-4">Top Motivos de Rejeição</h2>
          {rejectionReasons && rejectionReasons.length > 0 ? (
            <div className="space-y-3">
              {rejectionReasons.map((reason, index) => (
                <div key={index} className="flex items-center gap-3">
                  <span className="text-sm text-surface-500 w-6">{index + 1}.</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-surface-100">{reason.reason}</span>
                      <span className="text-sm text-surface-400">{reason.count} ({reason.percentage.toFixed(1)}%)</span>
                    </div>
                    <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-red-500 rounded-full"
                        style={{ width: `${reason.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-surface-500 text-center py-8">Nenhuma rejeição no período</p>
          )}
        </div>

        {/* Quick Actions */}
        <div className="glass rounded-xl p-6">
          <h2 className="text-lg font-semibold text-surface-100 mb-4">Ações Rápidas</h2>
          <div className="grid grid-cols-2 gap-3">
            <Link
              to="/admin/kyc/queue"
              className="p-4 bg-surface-800 rounded-lg hover:bg-surface-700 transition-colors group"
            >
              <FileText className="w-6 h-6 text-primary-400 mb-2" />
              <h3 className="font-medium text-surface-100 group-hover:text-primary-400 transition-colors">
                Fila de Análise
              </h3>
              <p className="text-xs text-surface-500 mt-1">
                {metrics?.pending || 0} pendentes
              </p>
            </Link>
            <Link
              to="/admin/kyc/analysts"
              className="p-4 bg-surface-800 rounded-lg hover:bg-surface-700 transition-colors group"
            >
              <Users className="w-6 h-6 text-green-400 mb-2" />
              <h3 className="font-medium text-surface-100 group-hover:text-green-400 transition-colors">
                Analistas
              </h3>
              <p className="text-xs text-surface-500 mt-1">
                Produtividade e atribuições
              </p>
            </Link>
            <Link
              to="/admin/kyc/templates"
              className="p-4 bg-surface-800 rounded-lg hover:bg-surface-700 transition-colors group"
            >
              <FileText className="w-6 h-6 text-yellow-400 mb-2" />
              <h3 className="font-medium text-surface-100 group-hover:text-yellow-400 transition-colors">
                Templates
              </h3>
              <p className="text-xs text-surface-500 mt-1">
                Templates de rejeição
              </p>
            </Link>
            <Link
              to="/admin/kyc/reports"
              className="p-4 bg-surface-800 rounded-lg hover:bg-surface-700 transition-colors group"
            >
              <BarChart3 className="w-6 h-6 text-purple-400 mb-2" />
              <h3 className="font-medium text-surface-100 group-hover:text-purple-400 transition-colors">
                Relatórios
              </h3>
              <p className="text-xs text-surface-500 mt-1">
                Exportar e agendar
              </p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  title,
  value,
  change,
  icon: Icon,
  color,
}: {
  title: string;
  value: number | string;
  change?: number;
  icon: React.ElementType;
  color: 'blue' | 'green' | 'cyan' | 'purple' | 'yellow' | 'red';
}) {
  const colorClasses = {
    blue: 'text-blue-400 bg-blue-500/10',
    green: 'text-green-400 bg-green-500/10',
    cyan: 'text-cyan-400 bg-cyan-500/10',
    purple: 'text-purple-400 bg-purple-500/10',
    yellow: 'text-yellow-400 bg-yellow-500/10',
    red: 'text-red-400 bg-red-500/10',
  };

  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-surface-400">{title}</span>
        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", colorClasses[color])}>
          <Icon className={cn("w-4 h-4", colorClasses[color].split(' ')[0])} />
        </div>
      </div>
      <div className="flex items-end justify-between">
        <span className="text-2xl font-bold text-surface-100">{value}</span>
        {change !== undefined && (
          <div className={cn(
            "flex items-center gap-1 text-sm",
            change >= 0 ? "text-green-400" : "text-red-400"
          )}>
            {change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {Math.abs(change)}%
          </div>
        )}
      </div>
    </div>
  );
}

export default KycDashboard;
